import {useMemo, useState} from 'react';
import {DeleteOutlined} from '@ant-design/icons';
import {Avatar, Button, theme, Typography} from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {CommentDto} from '../../../../../PRMGApiClient';

dayjs.extend(relativeTime);

const {Text} = Typography;

interface CommentContainerProps {
  comment: CommentDto;
  isAssignedToMe: boolean;
  onEdit: ({commentId, value}: {commentId: number; value: string}) => void;
  onDelete: (id: number) => void;
}

export const CommentContainer: React.FunctionComponent<CommentContainerProps> = ({
  comment: {id, authorName, modifiedDate, value, createdDate},
  isAssignedToMe,
  onEdit,
  onDelete,
}) => {
  const {token} = theme.useToken();

  const [editableText, setEditableText] = useState<string>(value ?? '');

  const initials = useMemo(() => {
    const names = authorName?.split(' ');
    return names?.length ? `${names[0][0]}${names[1][0]}` : '';
  }, [authorName]);

  return (
    <div
      className="flex flex-col gap-2 border"
      style={{
        padding: token.paddingSM,
        borderColor: token.colorBorderSecondary,
        borderRadius: token.borderRadiusLG,
        boxShadow: token.boxShadow,
      }}
    >
      <div className="flex justify-between w-full">
        <div className="flex gap-2 items-center">
          <Avatar shape="square" size="small" style={{color: token.blue, backgroundColor: token['blue-1']}}>
            {initials}
          </Avatar>
          <Text strong>{authorName}</Text>
          <Text type="secondary" className="place-self-end">
            {modifiedDate ? 'modified ' : 'created '}
            {dayjs(modifiedDate ?? createdDate).fromNow(true)}
          </Text>
        </div>
        {isAssignedToMe && (
          <Button size="small" type="text" icon={<DeleteOutlined />} onClick={() => id && onDelete(id)} />
        )}
      </div>
      <Text
        editable={
          isAssignedToMe && {
            onChange: (text: string) => {
              setEditableText(text);
              id && onEdit({commentId: id, value: text});
            },
          }
        }
      >
        {editableText}
      </Text>
    </div>
  );
};
